import { Component, OnInit } from '@angular/core';
import { PillarLayout, PillarConfig } from '../../../widgets/pillar-layout/pillar-layout';

import { MetaDataService } from '../../../core/services/meta-data.service';
import { SeoTitleStrategy } from '../../../core/seo/seo-title-strategy';

import { ModernCta } from '../../../widgets/modern-cta/modern-cta';

@Component({
  selector: 'app-what-is-incident-management',
  imports: [PillarLayout, ModernCta],
  templateUrl: './what-is-incident-management.html',
  styleUrl: './what-is-incident-management.scss',
})
export class WhatIsIncidentManagement implements OnInit {
  accentColor = '#FF932C';

  config: PillarConfig = {
    tocItems: [
      { id: 'what-is-incident-management', label: 'What is Incident Management?' },
      { id: 'incident-management-process', label: 'Incident Management Process' },
      { id: 'incident-vs-problem-vs-change', label: 'Incident vs Problem vs Change' },
      { id: 'metrics', label: 'Incident Management Metrics' },
      { id: 'faq', label: 'Frequently Asked Questions' },
    ],
    definition: {
      id: 'what-is-incident-management',
      eyebrow: 'Definition',
      title: 'What is Incident Management?',
      definitionHtml: `Incident management is the IT process of detecting, logging, classifying, resolving, and closing unplanned disruptions to IT services - restoring normal service operation as quickly as possible while minimising impact on the business.`,
      definitionCite: '- ITIL Incident Management - Complete Guide',
      bodyHtml: [
        `An incident is any unplanned interruption to an IT service or reduction in its quality - a server going down, an application becoming unavailable, or a user being unable to access a critical system. Incident management provides the structured process for responding to these events consistently and efficiently. `,
        `In ITIL, incident management is one of the core service operation processes, distinct from problem management (which finds root causes) and change management (which controls planned changes).`,
      ],
    },
    processes: {
      id: 'incident-management-process',
      eyebrow: 'Processes',
      title: 'Incident Management Process - 7 Steps',
      intro:
        'ITSM is not a single process - it is an interconnected set of disciplines that govern how IT services are delivered, maintained, and improved across the organisation.',
      items: [
        {
          icon: 'assets/img/feature-imgs/itsm/sass-icons/pillar-incident-mgmt.svg',
          title: 'Detection',
          description:
            'Incident identified - via monitoring alert, user report, or automated event',
        },
        {
          icon: 'assets/img/feature-imgs/itsm/sass-icons/pillar-problem-mgmt.svg',
          title: 'Logging ',
          description: 'Incident recorded in the service desk with all available details',
        },
        {
          icon: 'assets/img/feature-imgs/itsm/sass-icons/pillar-change-mgmt.svg',
          title: 'Classification ',
          description: 'Incident categorised by type, urgency, and impact to determine priority',
        },
        {
          icon: 'assets/img/feature-imgs/itsm/sass-icons/pillar-srm.svg',
          title: 'Assignment ',
          description: 'Routed to the appropriate team or individual based on category and skill',
        },
        {
          icon: 'assets/img/feature-imgs/itsm/sass-icons/pillar-knowledge-mgmt.svg',
          title: 'Investigation',
          description: 'Root cause diagnosed and workaround or fix identified',
        },
        {
          icon: 'assets/img/feature-imgs/itsm/sass-icons/pillar-sla-mgmt.svg',
          title: 'Resolution ',
          description: 'Service restored - fix applied or workaround implemented',
        },
        {
          icon: 'assets/img/feature-imgs/itsm/sass-icons/pillar-service-mgmt.svg',
          title: 'Closure',
          description:
            'Incident closed after user confirmation, record updated with resolution details',
        },
        // { icon: 'assets/img/feature-imgs/itsm/sass-icons/pillar-release-mgmt.svg',   title: 'Release Management',         description: 'Planning and controlling the deployment of software and infrastructure changes to production environments.' },
      ],
    },
    compare: {
      id: 'incident-vs-problem-vs-change',
      eyebrow: 'Comparisons',
      title: 'Incident vs Problem vs Change',
      intro: '',
      rows: [
        {
          term: '',
          termFull: 'Incident',
          tagClass: 'blue',
          manages: 'An unplanned disruption to a service - focus is on restoring service quickly',
          focus: '',
        },
        {
          term: '',
          termFull: 'Problem',
          tagClass: 'orange',
          manages:
            'The underlying root cause of one or more incidents - focus is on permanent elimination',
          focus: '',
        },
        {
          term: '',
          termFull: 'Change',
          tagClass: 'teal',
          manages:
            'A planned modification to the IT environment - focus is on controlled implementation',
          focus: '',
        },
        {
          term: '',
          termFull: 'Realtionship',
          tagClass: 'teal',
          manages:
            'Incidents trigger problem investigations; problems may result in change requests',
          focus: '',
        },
      ],
    },
    benefits: {
      id: 'metrics',
      eyebrow: 'Metrics',
      title: 'Incident Management Metrics',
      intro: '',
      items: [
        {
          num: '01',
          title: 'Mean Time to Detect (MTTD)',
          description: 'Average time from incident occurrence to detection',
        },
        {
          num: '02',
          title: 'Mean Time to Acknowledge (MTTA)',
          description: 'Average time from detection to assignment',
        },
        {
          num: '03',
          title: 'Mean Time to Resolve (MTTR)',
          description: 'Average time from detection to full service restoration',
        },
        {
          num: '04',
          title: 'First Call Resolution (FCR):',
          description: 'Percentage of incidents resolved at the first point of contact',
        },
        {
          num: '05',
          title: 'SLA compliance rate',
          description: 'Percentage of incidents resolved within defined service level targets',
        },
      ],
    },
    faq: {
      id: 'faq',
      eyebrow: 'FAQ',
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'What is a major incident?',
          open: true,
          answer:
            'A major incident is a high-impact, high-urgency incident that causes significant disruption to critical business services. Major incidents typically require a dedicated response team, a separate communication channel, and a post-incident review (PIR) after resolution.',
        },
        {
          question: 'What is the difference between incident and problem management?',
          open: false,
          answer: `Incident management focuses on restoring service as quickly as possible - the immediate response. Problem management focuses on finding and eliminating the root cause to prevent the incident from recurring. An incident is the symptom; the problem is the underlying cause.`,
        },
        {
          question: 'What is an incident response calculator?',
          open: false,
          answer:
            'An incident response calculator estimates the cost and time impact of IT incidents based on factors like affected users, hourly business cost, and average resolution time. It helps IT leaders quantify the business value of improving incident management processes.',
        },
        {
          question: 'What is SLA in incident management?',
          open: false,
          answer:
            'An SLA (Service Level Agreement) in incident management defines the target response and resolution times for incidents based on their priority. For example, a P1 critical incident might have a 15-minute response SLA and a 4-hour resolution SLA, while a P3 low-priority incident might have a 4-hour response and 5-day resolution target.',
        },
        {
          question: 'What is the incident management process?',
          open: false,
          answer:
            'The ITIL incident management process follows 7 steps: (1) Detection - the incident is identified via monitoring alert, user report, or automated event; (2) Logging - the incident is recorded in the service desk with all available details; (3) Classification - categorised by type, urgency, and impact to determine priority; (4) Assignment - routed to the appropriate team or individual; (5) Investigation - root cause diagnosed and workaround or fix identified; (6) Resolution - service restored by applying the fix or workaround; (7) Closure - incident closed after user confirmation and the record updated with resolution details.',
        },
        {
          question: 'What are incident management tools?',
          open: false,
          answer:
            'Incident management tools are IT Service Management (ITSM) platforms that centralise the detection, logging, routing, resolution, and tracking of IT incidents. Key features to look for include automated ticket creation from monitoring alerts, SLA tracking and escalation, priority-based routing, a knowledge base for known fixes, real-time dashboards for MTTR and FCR metrics, and integration with monitoring and communication tools. Examples include ITSM platforms like Infraon, ServiceNow, and Jira Service Management.',
        },
      ],
    },
  };

  constructor(
    private metaService: MetaDataService,
    private seoStrategy: SeoTitleStrategy,
  ) {
    const ratingSchema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': 'https://infraon.io/itsm-tool#software',
      name: 'Infraon ITSM Tool',
      url: 'https://infraon.io/itsm-tool',
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'IT Service Management Software',
      operatingSystem: 'Web',
      description:
        'Infraon ITSM Tool uses AI to Auto-route Tickets, Auto-generate KB, and enforce SLA Escalations Automatically. Trusted by IT teams across India, Saudi Arabia, UAE & SEA.',
      featureList: [
        'Incident Management',
        'Email to Incident',
        'Self-Service Portal',
        'Standard SLA Management',
        'Workflow Automator',
        'Request Management',
        'Multiple SLA Profiles',
        'Marketplace Access for 3rd Party Integrations',
        'Problem Management',
        'Release Management',
        'Change Management',
        'AI-assisted Knowledge Base Creation',
      ],
      publisher: {
        '@type': 'Organization',
        name: 'Infraon',
        url: 'https://infraon.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://infraon.io/assets/img/logos/website/org-logo.jpg',
          width: 200,
          height: 60,
        },
      },
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '19.00',
        highPrice: '57.00',
        priceCurrency: 'USD',
        offerCount: '3',
        offers: [
          {
            '@type': 'Offer',
            name: 'Essential',
            description:
              'For small IT teams that need fast, reliable service delivery. Includes Incident Management, Email to Incident, Self-Service Portal, Standard SLA Management, and Workflow Automator.',
            price: '19.00',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '19.00',
              priceCurrency: 'USD',
              unitText: 'per user / per month',
            },
            url: 'https://infraon.io/itsm-tool#pricing',
            eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
          },
          {
            '@type': 'Offer',
            name: 'Standard',
            description:
              'For scaling teams that need smarter workflows and AI-powered support. Includes everything in Essential plus Request Management, Multiple SLA Profiles, and Marketplace Access for 3rd Party Integrations.',
            price: '47.00',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '47.00',
              priceCurrency: 'USD',
              unitText: 'per user / per month',
            },
            url: 'https://infraon.io/itsm-tool#pricing',
            eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
          },
          {
            '@type': 'Offer',
            name: 'Professional',
            description:
              'For enterprises that need full ITIL coverage and advanced automation. Includes everything in Standard plus Problem Management, Release Management, Change Management, and AI-assisted Knowledge Base Creation.',
            price: '57.00',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '57.00',
              priceCurrency: 'USD',
              unitText: 'per user / per month',
            },
            url: 'https://infraon.io/itsm-tool#pricing',
            eligibleCustomerType: 'http://purl.org/goodrelations/v1#Business',
          },
        ],
      },
      audience: {
        '@type': 'Audience',
        audienceType:
          'IT Teams, IT Managers, IT Analysts, Enterprise IT Teams, ITSM Consultants, IT Operations Managers',
      },
    };

    this.seoStrategy.injectratingSchema('1', JSON.stringify(ratingSchema));

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': 'https://infraon.io/itsm-tool/what-is-incident-management#article',
      headline: 'What is Incident Management? ITIL Process Explained',
      description:
        'Learn what incident management is, the 7-step process, key metrics like MTTR, what an incident is, and which tools IT teams use to resolve disruptions faster.',
      url: 'https://infraon.io/itsm-tool/what-is-incident-management',
      inLanguage: 'en',
      author: {
        '@type': 'Organization',
        name: 'Infraon',
        url: 'https://infraon.io',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Infraon',
        url: 'https://infraon.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://infraon.io/assets/img/logos/website/org-logo.jpg',
          width: 200,
          height: 60,
        },
      },
      image: {
        '@type': 'ImageObject',
        url: 'https://infraon.io/assets/img/feature-imgs/itsm/what-is-incident-management-og.png',
        width: 1200,
        height: 630,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': 'https://infraon.io/itsm-tool/what-is-incident-management',
      },
      about: [
        {
          '@type': 'Thing',
          name: 'Incident Management',
          sameAs: 'https://en.wikipedia.org/wiki/Incident_management',
        },
        {
          '@type': 'Thing',
          name: 'ITIL',
          sameAs: 'https://en.wikipedia.org/wiki/ITIL',
        },
        {
          '@type': 'Thing',
          name: 'IT Service Management',
          alternateName: 'ITSM',
          sameAs: 'https://en.wikipedia.org/wiki/IT_service_management',
        },
      ],
      keywords: [
        'what is incident management',
        'what is an incident in incident management',
        'what is incident management process',
        'what are incident management tools',
        'ITIL incident management',
        'incident management steps',
        'MTTR',
        'mean time to resolve',
        'major incident',
        'SLA incident management',
      ],
      articleSection: [
        'What is Incident Management',
        'What is an Incident',
        'Incident Management Process',
        'Incident Management Metrics',
        'Incident Management Tools',
        'Incident Management FAQ',
      ],
      isPartOf: {
        '@type': 'WebSite',
        name: 'Infraon',
        url: 'https://infraon.io',
      },
      mentions: [
        {
          name: 'Infraon ITSM Tool',
          url: 'https://infraon.io/itsm-tool',
        },
      ],
    };

    this.seoStrategy.injectratingSchema(
      'incident-management-article-schema',
      JSON.stringify(articleSchema),
    );
  }

  ngOnInit(): void {
    this.metaService.updateFaqJsonLd(
      this.config.faq.items.map((f) => ({
        question: f.question,
        answer: { paragraphs: [f.answer] },
      })),
    );
  }
}
